import i18n from '@/locales/config';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App, ConfigProvider, ConfigProviderProps, theme } from 'antd';
import enUS from 'antd/locale/en_US';
import vi_VN from 'antd/locale/vi_VN';
import zhCN from 'antd/locale/zh_CN';
import zh_HK from 'antd/locale/zh_HK';
import pt_BR from 'antd/lib/locale/pt_BR';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import React, { ReactNode, useEffect, useState } from 'react';
import { ThemeProvider, useTheme } from './components/theme-provider';
import { TooltipProvider } from './components/ui/tooltip';
import storage from './utils/authorization-util';
import { mcpToolService } from '@/services/mcpToolService';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

const AntLanguageMap = {
  en: enUS,
  zh: zhCN,
};

const queryClient = new QueryClient();

type Locale = ConfigProviderProps['locale'];

function Root({ children }: React.PropsWithChildren) {
  const { theme: themegraphrag } = useTheme();
  const getLocale = (lng: string) =>
    AntLanguageMap[lng as keyof typeof AntLanguageMap] ?? zhCN;

  const [locale, setLocal] = useState<Locale>(getLocale(storage.getLanguage()));

  i18n.on('languageChanged', function (lng: string) {
    storage.setLanguage(lng);
    setLocal(getLocale(lng));
  });

  return (
    <>
      <ConfigProvider
        theme={{
          token: {
            fontFamily: 'Inter',
          },
          algorithm:
            themegraphrag === 'dark'
              ? theme.darkAlgorithm
              : theme.defaultAlgorithm,
        }}
        locale={locale}
      >
        <App> {children}</App>
      </ConfigProvider>
      <ReactQueryDevtools buttonPosition={'top-left'} />
    </>
  );
}

const RootProvider = ({ children }: React.PropsWithChildren) => {
  const [toolsLoaded, setToolsLoaded] = useState(false);
  
  useEffect(() => {
    // 设置语言
    const lng = storage.getLanguage();
    if (lng) {
      i18n.changeLanguage(lng);
    }
    
    // ✅ 统一初始化工具服务（只调用一次 initialize）
    const initializeTools = async () => {
      try {
        console.log('🚀 [App] 应用启动，初始化工具服务...');
        
        // 调用统一的 initialize 方法
        await mcpToolService.initialize();
        
        // 可选：打印额外的统计信息
        const stats = mcpToolService.getStatistics();
        console.log('✅ [App] 应用初始化完成');
        
        setToolsLoaded(true);
      } catch (error) {
        console.error('❌ [App] 初始化失败:', error);
        // 即使失败也设置为已加载，避免阻塞应用
        setToolsLoaded(true);
      }
    };
    
    initializeTools();
  }, []);

  // 可选：显示加载状态
  // if (!toolsLoaded) {
  //   return (
  //     <div style={{ 
  //       display: 'flex', 
  //       flexDirection: 'column',
  //       justifyContent: 'center', 
  //       alignItems: 'center', 
  //       height: '100vh',
  //       gap: '16px'
  //     }}>
  //       <div style={{ fontSize: '24px' }}>⚙️</div>
  //       <div>正在初始化工具服务...</div>
  //     </div>
  //   );
  // }

  return (
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="graphrag-ui-theme">
          <Root>{children}</Root>
        </ThemeProvider>
      </QueryClientProvider>
    </TooltipProvider>
  );
};

export function rootContainer(container: ReactNode) {
  return <RootProvider>{container}</RootProvider>;
}
